// Shell tool — one-shot shell commands for `local` workspaces (Revolt Desktop IPC).
// Extends WorkspaceTool; `cwd` is always the app project root. No persistent shell.
// The desktop host picks the shell via env (see Revolt Desktop WorkspaceLocalAppManager).

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class ShellTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = "shellTool";
  }

  /**
   * Run a single command in the workspace project directory (not a live REPL; each call is fresh).
   * The desktop host picks one effective shell (same idea as Cursor’s default terminal); there is
   * no separate tool parameter for shell profile—pass the command line you would type there.
   * @param {string} command - Full command line for that shell
   * @returns {Promise<Object>} Tool result envelope
   */
  async runShellCommand(command) {
    try {
      if (this.appType === "browser") {
        return this.returnError(
          {},
          "Shell commands are only available for local workspaces (Revolt Desktop) with a project folder on disk."
        );
      }

      if (this.appType === "local") {
        return await this.runShellCommandForLocal(command);
      }

      return this.returnError(
        {},
        `App type '${this.appType}' does not support shell commands`
      );
    } catch (error) {
      logger.error(
        { tags: "tools|shell|error", color1: "red" },
        "Failed to run shell command",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  async runShellCommandForLocal(command) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.runShellCommand) {
      return this.returnError(
        {},
        "Shell commands require Revolt Desktop (workspaceLocalAppAPI.runShellCommand)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const cmd = command == null ? "" : String(command).trim();
    if (!cmd) {
      return this.returnError({}, "command is required");
    }

    const res = await api.runShellCommand({ rootPath: root, command: cmd });

    if (!res?.success) {
      return this.returnError(
        {
          stdout: res?.stdout,
          stderr: res?.stderr,
          exitCode: res?.exitCode ?? null,
          timedOut: res?.timedOut,
          truncated: res?.truncated,
        },
        res?.error || "Command failed"
      );
    }

    return this.returnSuccess(
      {
        stdout: res.stdout || "",
        stderr: res.stderr || "",
        exitCode: typeof res.exitCode === "number" ? res.exitCode : 0,
        truncated: !!res.truncated,
      },
      "Command completed"
    );
  }
}
